import axios from 'axios';

/**
 * Supplier Service
 * Handles all API calls related to suppliers
 */
export const supplierService = {
  /**
   * Get all suppliers
   * @param {Object} params - Query parameters (filters, pagination, etc.)
   * @returns {Promise<Object>} Suppliers data with pagination
   */
  async getAllSuppliers(params = {}) {
    try {
      const response = await axios.get('/suppliers', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  /**
   * Get a single supplier by ID
   * @param {string} supplierId - Supplier ID
   * @returns {Promise<Object>} Supplier data
   */
  async getSupplierById(supplierId) {
    try {
      const response = await axios.get(`/suppliers/${supplierId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error;
    }
  },

  /**
   * Create a new supplier
   * @param {Object} supplierData - Supplier data
   * @returns {Promise<Object>} Created supplier
   */
  async createSupplier(supplierData) {
    try {
      const response = await axios.post('/suppliers', supplierData);
      return response.data;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  },

  /**
   * Update a supplier
   * @param {string} supplierId - Supplier ID
   * @param {Object} supplierData - Updated supplier data
   * @returns {Promise<Object>} Updated supplier
   */
  async updateSupplier(supplierId, supplierData) {
    try {
      const response = await axios.patch(`/suppliers/${supplierId}`, supplierData);
      return response.data;
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  },

  /**
   * Delete a supplier
   * @param {string} supplierId - Supplier ID
   * @returns {Promise<void>}
   */
  async deleteSupplier(supplierId) {
    try {
      await axios.delete(`/suppliers/${supplierId}`);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  },

  /**
   * Test supplier webhook configuration
   * @param {string} supplierId - Supplier ID
   * @returns {Promise<Object>} Test result
   */
  async testWebhook(supplierId) {
    try {
      const response = await axios.post(`/suppliers/${supplierId}/test-webhook`);
      return response.data;
    } catch (error) {
      console.error('Error testing supplier webhook:', error);
      throw error;
    }
  },

  /**
   * Get supplier templates (for in-house export)
   * @param {string} supplierId - Supplier ID
   * @returns {Promise<Object>} Supplier template configuration
   */
  async getSupplierTemplate(supplierId) {
    try {
      const response = await axios.get(`/suppliers/${supplierId}/template`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier template:', error);
      throw error;
    }
  },

  /**
   * Update supplier template (for in-house export)
   * @param {string} supplierId - Supplier ID
   * @param {Object} templateData - Template configuration
   * @returns {Promise<Object>} Updated template
   */
  async updateSupplierTemplate(supplierId, templateData) {
    try {
      const response = await axios.patch(`/suppliers/${supplierId}/template`, templateData);
      return response.data;
    } catch (error) {
      console.error('Error updating supplier template:', error);
      throw error;
    }
  },

  async updateCustomerConfig(supplierId, customerConfig) {
    try {
      const response = await axios.patch(
        `/suppliers/${supplierId}/customer-config`,
        customerConfig
      );
      return response.data;
    } catch (error) {
      console.error('Error updating customer config:', error);
      throw error;
    }
  },
};

export default supplierService;
